/**
 * Cloud UI: account chip with dropdown (header) + always-visible session
 * library (left sidebar) + ✨ Generate. Configured via window.KALMTIDE_CONFIG;
 * with no apiBase the builder stays a fully offline tool (no cloud chrome).
 */

import type { Preset } from "../../schema/types.ts";
import { validatePreset } from "../../schema/validator.ts";
import { ApiClient, ApiError, type CloudPresetEntry } from "./api.ts";

export interface KalmTideConfig {
  apiBase?: string;
  googleClientId?: string;
}

declare global {
  interface Window {
    KALMTIDE_CONFIG?: KalmTideConfig;
  }
}

export interface CloudCallbacks {
  onStatus(text: string, isError?: boolean): void;
  onLoadPreset(preset: Preset): void;
  currentPreset(): Preset | null;
}

export interface CloudMounts {
  header: HTMLElement;
  sidebar: HTMLElement;
}

const GOAL_ACCENT: Record<string, string> = {
  sleep: "#8fa8ff",
  meditate: "#6fd9c9",
  focus: "#ffd479",
  obe: "#c09cff",
};

/** What the rest of the studio may ask of the cloud once it's configured. */
export interface CloudHandle {
  /** Synthesize an AI voice — PCM16 WAV bytes. Throws when signed out. */
  ttsFetch(text: string, voice: string): Promise<ArrayBuffer>;
  signedIn(): boolean;
}

export function setupCloud(mounts: CloudMounts, callbacks: CloudCallbacks): CloudHandle | null {
  const config = window.KALMTIDE_CONFIG ?? {};
  if (!config.apiBase) return null; // offline-first: no backend, no cloud chrome

  const api = new ApiClient(config.apiBase, localStorage);
  let entries: CloudPresetEntry[] = [];
  let libraryBusy = false;

  const wrapErrors = (fn: () => Promise<void>) => (): void => {
    void fn().catch((err: unknown) => {
      const detail =
        err instanceof ApiError && err.details.length > 0 ? `\n${err.details.slice(0, 3).join("\n")}` : "";
      callbacks.onStatus(`${err instanceof Error ? err.message : String(err)}${detail}`, true);
      renderAll();
    });
  };

  // Returning from the server-side OAuth redirect? The session rides in the
  // URL fragment (never sent to any server). Adopt it and clean the URL.
  const returnMatch = /wk_session=([0-9a-f]{64})&wk_email=([^&]*)/.exec(location.hash);
  if (returnMatch) {
    api.setSession(returnMatch[1]!, decodeURIComponent(returnMatch[2]!));
    history.replaceState(null, "", location.pathname + location.search);
    callbacks.onStatus(`signed in as ${api.email}`);
    // The redirect fragment carries no plan — fetch it, then re-badge.
    void api.me().then(renderHeader).catch(() => {});
    const pending = sessionStorage.getItem("kalmtide.pendingPreset");
    if (pending) {
      sessionStorage.removeItem("kalmtide.pendingPreset");
      const check = validatePreset(JSON.parse(pending));
      if (check.ok) callbacks.onLoadPreset(check.preset);
    }
  }

  const ensureSignedIn = async (): Promise<void> => {
    if (api.signedIn) return;
    callbacks.onStatus("redirecting to Google sign-in…");
    const current = callbacks.currentPreset();
    if (current) sessionStorage.setItem("kalmtide.pendingPreset", JSON.stringify(current));
    const returnTo = encodeURIComponent(location.origin + location.pathname);
    location.assign(`${config.apiBase}/auth/google/start?return=${returnTo}`);
    await new Promise<never>(() => {}); // navigation takes over
  };

  const refreshLibrary = async (): Promise<void> => {
    if (!api.signedIn) return;
    libraryBusy = true;
    renderSidebar();
    try {
      entries = (await api.listPresets()).filter((e) => e.preset !== null);
    } finally {
      libraryBusy = false;
      renderSidebar();
    }
  };

  const saveCurrent = async (): Promise<void> => {
    await ensureSignedIn();
    const preset = callbacks.currentPreset();
    if (!preset) throw new Error("current session is invalid — fix the errors first");
    await api.putPreset(preset);
    callbacks.onStatus(`saved "${preset.meta.name}" to your library`);
    await refreshLibrary();
  };

  const generate = async (): Promise<void> => {
    await ensureSignedIn();
    const prompt = window.prompt(
      "Describe your session — goal, duration, mood.\ne.g. \"calm before a meeting, 10 minutes, soft noise\"",
    );
    if (!prompt) return;
    callbacks.onStatus("generating…");
    const result = await api.generate(prompt);
    const check = validatePreset(result.preset);
    if (!check.ok) throw new ApiError(500, "server returned an invalid preset", check.errors);
    callbacks.onLoadPreset(check.preset);
    callbacks.onStatus(result.cached ? "generated (cached)" : "generated ✓");
  };

  // ------------------------------------------------------------- header

  function renderHeader(): void {
    mounts.header.replaceChildren();

    const genBtn = document.createElement("button");
    genBtn.className = "accent";
    genBtn.textContent = "✨ Generate";
    genBtn.addEventListener("click", wrapErrors(generate));
    mounts.header.append(genBtn);

    if (!api.signedIn) {
      const signIn = document.createElement("button");
      signIn.textContent = "Sign in";
      signIn.addEventListener("click", wrapErrors(ensureSignedIn));
      mounts.header.append(signIn);
      return;
    }

    // Account chip with dropdown (email · plan · sign out).
    const email = api.email ?? "account";
    const wrap = document.createElement("div");
    wrap.className = "menu account";
    const chip = document.createElement("button");
    chip.className = "account-chip";
    chip.innerHTML =
      `<span class="avatar">${escapeHtml(email.charAt(0).toUpperCase())}</span>` +
      `<span class="account-name">${escapeHtml(email.split("@")[0] ?? email)}</span>` +
      (api.plan === "cloud" ? `<span class="plan-pill">Cloud</span>` : "") +
      `<span class="caret">▾</span>`;
    const panel = document.createElement("div");
    panel.className = "menu-panel";
    const emailRow = document.createElement("div");
    emailRow.className = "menu-caption";
    emailRow.textContent = email + (api.plan === "cloud" ? " · Cloud plan" : "");
    const sep = document.createElement("div");
    sep.className = "menu-sep";
    const signOut = document.createElement("button");
    signOut.className = "menu-item";
    signOut.textContent = "Sign out";
    signOut.addEventListener("click", () => {
      api.signOut();
      entries = [];
      callbacks.onStatus("signed out");
      renderAll();
    });
    // Account deletion — Play's account-deletion policy requires a WEB path;
    // this menu item is it (privacy.html points here). A real modal, not
    // window.prompt: browsers can silently suppress native dialogs, which
    // made the whole flow no-op with only a subtle toast.
    const delSep = document.createElement("div");
    delSep.className = "menu-sep";
    const delAccount = document.createElement("button");
    delAccount.className = "menu-item danger";
    delAccount.textContent = "Delete account…";
    delAccount.addEventListener(
      "click",
      wrapErrors(async () => {
        if (!(await confirmAccountDeletion(email))) {
          callbacks.onStatus("account deletion cancelled");
          return;
        }
        await api.deleteAccount();
        entries = [];
        callbacks.onStatus("account deleted — everything server-side is gone");
        renderAll();
      }),
    );
    panel.append(emailRow, sep, signOut, delSep, delAccount);
    chip.addEventListener("click", (e) => {
      e.stopPropagation();
      wrap.classList.toggle("open");
    });
    document.addEventListener("click", () => wrap.classList.remove("open"));
    wrap.append(chip, panel);
    mounts.header.append(wrap);
  }

  // ------------------------------------------------------------ sidebar

  function renderSidebar(): void {
    const box = mounts.sidebar;
    box.replaceChildren();

    const head = document.createElement("div");
    head.className = "lib-head";
    const title = document.createElement("span");
    title.className = "lib-title";
    title.textContent = "Library";
    head.append(title);
    if (api.signedIn) {
      const refresh = document.createElement("button");
      refresh.className = "mini";
      refresh.title = "Refresh";
      refresh.textContent = libraryBusy ? "…" : "⟳";
      refresh.addEventListener("click", wrapErrors(refreshLibrary));
      head.append(refresh);
    }
    box.append(head);

    if (!api.signedIn) {
      const card = document.createElement("div");
      card.className = "lib-signin";
      const p = document.createElement("p");
      p.textContent = "Sign in to keep your sessions in the cloud and play them on your phone.";
      const btn = document.createElement("button");
      btn.className = "accent";
      btn.textContent = "Sign in with Google";
      btn.addEventListener("click", wrapErrors(ensureSignedIn));
      card.append(p, btn);
      box.append(card);
      return;
    }

    const save = document.createElement("button");
    save.className = "lib-save";
    save.textContent = "＋ Save current session";
    save.addEventListener("click", wrapErrors(saveCurrent));
    box.append(save);

    if (entries.length === 0 && !libraryBusy) {
      const empty = document.createElement("p");
      empty.className = "lib-empty";
      empty.textContent = "Nothing here yet — save your first session.";
      box.append(empty);
    }

    for (const entry of entries) {
      const preset = entry.preset!;
      const row = document.createElement("div");
      row.className = "lib-row";
      const item = document.createElement("button");
      item.className = "lib-item";
      const accent = GOAL_ACCENT[preset.meta.goal] ?? "#8fa8ff";
      const mins = Math.round(preset.meta.durationSec / 60);
      const when = new Date(entry.updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" });
      item.innerHTML =
        `<span class="lib-item-name">${escapeHtml(preset.meta.name)}</span>` +
        `<span class="lib-item-meta"><span class="goal-chip" style="--chip:${accent}">${preset.meta.goal}</span>` +
        `${mins} min · ${when}</span>`;
      item.addEventListener("click", () => {
        callbacks.onLoadPreset(preset);
        callbacks.onStatus(`loaded "${preset.meta.name}"`);
      });
      const del = document.createElement("button");
      del.className = "lib-del";
      del.title = `Delete "${preset.meta.name}"`;
      del.textContent = "✕";
      del.addEventListener("click", wrapErrors(async () => {
        // Soft-delete server-side (tombstoned) — but to the user it's gone
        // from every device, so ask.
        if (!confirm(`Delete "${preset.meta.name}" from your library?\nIt disappears on every synced device.`)) {
          return;
        }
        await api.deletePreset(entry.id);
        callbacks.onStatus(`deleted "${preset.meta.name}"`);
        await refreshLibrary();
      }));
      row.append(item, del);
      box.append(row);
    }
  }

  function renderAll(): void {
    renderHeader();
    renderSidebar();
  }

  renderAll();
  if (api.signedIn) {
    // On failure re-render: a 401 here clears the stored session, and the
    // header must not keep showing a signed-in chip for a dead session.
    void refreshLibrary().catch(() => renderAll());
    // Refresh the plan badge (the server lazily re-checks the subscription).
    void api.me().then(renderHeader).catch(() => renderAll());
  }

  return {
    ttsFetch: (text, voice) => api.tts(text, voice),
    signedIn: () => api.signedIn,
  };
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/**
 * In-page destructive confirm. Resolves true only on an explicit
 * "Delete forever" click; Escape, overlay click, and "Keep my account"
 * all resolve false.
 */
function confirmAccountDeletion(email: string): Promise<boolean> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "modal-overlay";
    const box = document.createElement("div");
    box.className = "modal-box";
    box.innerHTML = `
      <h3>Delete your account?</h3>
      <p class="modal-note">This permanently removes the account for
        <b>${escapeHtml(email)}</b> from our servers — synced sessions,
        purchase records and usage meters. Sessions saved on your devices
        stay on your devices.</p>
      <p class="modal-note">Subscribed to Cloud? Cancel it in Google Play
        first — deleting the account doesn't stop Play's billing.</p>
      <div class="modal-actions">
        <button data-act="keep">Keep my account</button>
        <button data-act="delete" class="danger-solid">Delete forever</button>
      </div>`;
    overlay.append(box);

    const done = (answer: boolean): void => {
      document.removeEventListener("keydown", onKey);
      overlay.remove();
      resolve(answer);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") done(false);
    };
    document.addEventListener("keydown", onKey);
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) done(false);
    });
    box.querySelector('[data-act="keep"]')!.addEventListener("click", () => done(false));
    box.querySelector('[data-act="delete"]')!.addEventListener("click", () => done(true));
    document.body.append(overlay);
  });
}
